/**
 * Form validation schemas using Zod
 */

import { z } from 'zod';

import { PROVINCES } from './provinces';

// ==========================================
// Common Schemas
// ==========================================

export const emailSchema = z.string().email('Email tidak valid');

export const phoneSchema = z.string().regex(
  /^(\+62|62|0)8[1-9][0-9]{6,10}$/,
  'Nomor telepon tidak valid'
);

export const urlSchema = z.string().url('URL tidak valid');

export const passwordSchema = z
  .string()
  .min(8, 'Password minimal 8 karakter')
  .regex(/[A-Z]/, 'Password harus mengandung huruf besar')
  .regex(/[a-z]/, 'Password harus mengandung huruf kecil')
  .regex(/[0-9]/, 'Password harus mengandung angka');

// ==========================================
// Authentication Schemas
// ==========================================

// POST /auth/login takes email + password (LoginRequest in the OpenAPI
// contract). It never accepted a username.
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password wajib diisi'),
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// POST /auth/register takes UserCreate: name + email + password. It never
// accepted a username, same mistake the login form used to make. The length
// bounds mirror UserCreate.name (2..100). confirmPassword is client-only and is
// dropped before the request.
export const registerSchema = z.object({
  name: z.string().min(2, 'Nama minimal 2 karakter').max(100, 'Nama maksimal 100 karakter'),
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Password tidak cocok',
  path: ['confirmPassword'],
});

export type RegisterFormData = z.infer<typeof registerSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Password lama wajib diisi'),
  newPassword: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Password tidak cocok',
  path: ['confirmPassword'],
});

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

// ==========================================
// Profile Schemas
// ==========================================

export const profileSchema = z.object({
  name: z.string().min(2, 'Nama minimal 2 karakter'),
  email: emailSchema,
  phone: phoneSchema.optional().or(z.literal('')),
  avatar: z.string().optional(),
});

export type ProfileFormData = z.infer<typeof profileSchema>;

// ==========================================
// E-commerce Schemas
// ==========================================

// Mirrors the backend platform enum exactly (EcommerceOrderResponse.platform).
// `other` is a real option there, so an arbitrary shop URL is allowed — the
// admin prices it by hand during the quote.
export const ecommercePlatformSchema = z.enum(['amazon', 'ebay', 'aliexpress', 'other']);

export type EcommercePlatformName = z.infer<typeof ecommercePlatformSchema>;

/**
 * Best-effort platform from a product URL, matched on the hostname so a path
 * segment like /amazon-basics-cable does not decide the platform. Falls back to
 * `other`, which the backend accepts.
 */
export function detectPlatform(url: string): EcommercePlatformName {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return 'other';
  }
  if (host.includes('amazon.')) return 'amazon';
  if (host.includes('ebay.')) return 'ebay';
  if (host.includes('aliexpress.')) return 'aliexpress';
  return 'other';
}

// Field names match EcommerceEstimateRequest: the endpoint needs the price and
// name as input because nothing scrapes the shop. Estimates are
// manual-assisted in v1.0 (roadmap §5).
export const priceEstimateSchema = z.object({
  item_url: urlSchema,
  item_name: z.string().min(1, 'Nama produk wajib diisi'),
  item_price_usd: z
    .number({ message: 'Harga wajib diisi' })
    .positive('Harga harus lebih dari 0'),
  platform: ecommercePlatformSchema.optional(),
});

export type PriceEstimateFormData = z.infer<typeof priceEstimateSchema>;

// ==========================================
// Helper Functions
// ==========================================

/**
 * Format Zod errors for display
 */
export function formatZodErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};

  error.issues.forEach((err) => {
    const path = err.path.join('.');
    if (!errors[path]) {
      errors[path] = err.message;
    }
  });

  return errors;
}

/**
 * Validate data against schema
 */
export function validateData<T extends z.ZodSchema>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return { success: false, errors: formatZodErrors(result.error) };
}

// ==========================================
// Jastip Quote Schemas (F2 — /jasa/belanja)
// ==========================================

/**
 * Nomor WhatsApp yang ditulis orang: `0812-8841-2270`, `+62 812 8841 2270`,
 * `(0812) 8841-2270`. Pemisahnya dibuang dulu, baru dicocokkan — menolak nomor
 * benar hanya karena ada tanda hubung adalah cara tercepat kehilangan calon
 * customer di titik paling akhir form.
 */
export const whatsappSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s().-]/g, ''))
  .refine((value) => /^(\+62|62|0)8[1-9][0-9]{6,10}$/.test(value), {
    message: 'Nomor WhatsApp tidak valid — contoh 0812-3456-7890',
  });

/**
 * Kontak menggantikan akun di alur quote-first: WhatsApp ATAU email, tidak
 * keduanya. Discriminated union supaya aturan validasinya ikut pilihan —
 * memilih "email" lalu mengisi nomor telepon harus gagal, bukan lolos karena
 * schema-nya `z.string()` yang longgar. Bentuknya sama persis dengan
 * `components['schemas']['Contact']`, jadi hasil parse langsung dikirim.
 */
export const jastipContactSchema = z.discriminatedUnion('channel', [
  z.object({ channel: z.literal('whatsapp'), value: whatsappSchema }),
  z.object({ channel: z.literal('email'), value: emailSchema }),
]);

export type JastipContact = z.infer<typeof jastipContactSchema>;

/**
 * Alamat kirim barang koleksi. Bentuk output-nya sama persis dengan
 * `components['schemas']['ShippingAddress']` (recipient_name, recipient_phone,
 * address, city, province, postal_code), jadi hasil parse dikirim apa adanya.
 * Batasannya mengikuti `ShippingAddress` di backend (`models/ecommerce.py`) —
 * kalau backend memperketat, di sinilah disamakan. Nomor HP dibersihkan dari
 * pemisah dulu, sama seperti `whatsappSchema`, supaya `0812-3456-7890` tidak
 * ditolak hanya karena tanda hubungnya.
 */
export const shippingAddressSchema = z.object({
  recipient_name: z
    .string()
    .trim()
    .min(2, 'Nama penerima minimal 2 karakter')
    .max(100, 'Nama penerima maksimal 100 karakter'),
  recipient_phone: z
    .string()
    .trim()
    .transform((value) => value.replace(/[\s().-]/g, ''))
    .refine((value) => /^(\+62|62|0)8[1-9][0-9]{6,10}$/.test(value), {
      message: 'Nomor HP tidak valid — contoh 0812-3456-7890',
    }),
  address: z
    .string()
    .trim()
    .min(10, 'Alamat minimal 10 karakter — tulis jalan, nomor, RT/RW, kecamatan')
    .max(500, 'Alamat maksimal 500 karakter'),
  city: z.string().trim().min(2, 'Kota/kabupaten wajib diisi').max(100, 'Maksimal 100 karakter'),
  // Daftar tertutup, bukan teks bebas: ongkir dihitung backend dari
  // provinsi ini (Pekanbaru -> tujuan), jadi ejaan yang tidak dikenali
  // berarti checkout gagal 422. Dropdown menutup kelasnya di sumber.
  province: z.enum(PROVINCES, { error: 'Pilih provinsi dari daftar' }),
  postal_code: z.string().trim().regex(/^\d{5}$/, 'Kode pos harus 5 digit'),
});

export type ShippingAddressInput = z.input<typeof shippingAddressSchema>;
export type ShippingAddressValues = z.output<typeof shippingAddressSchema>;

/**
 * Orang menempel `amazon.com/dp/B09XS7JWHH` dari bilah alamat HP, tanpa skema.
 * `z.url()` menolaknya. Menambahkan `https://` di sini lebih murah daripada
 * error "URL tidak valid" pada link yang sebetulnya benar.
 */
export const productUrlSchema = z
  .string()
  .trim()
  .min(1, 'Link barang wajib diisi')
  .transform((value) => (/^https?:\/\//i.test(value) ? value : `https://${value}`))
  .refine((value) => z.string().url().safeParse(value).success, { message: 'Link barang tidak valid' });

/**
 * Form `/jasa/belanja`. Nama field mengikuti `QuoteCreate` di kontrak OpenAPI
 * supaya hasil parse dikirim apa adanya tanpa lapisan pemetaan yang bisa basi.
 * `platform` tidak diminta ke user — ditebak dari hostname lewat
 * `detectPlatform`, dan backend menerima `other` kalau tokonya di luar daftar.
 */
export const jastipQuoteSchema = z.object({
  item_url: productUrlSchema,
  item_name: z.string().trim().min(1, 'Nama barang wajib diisi').max(200, 'Nama barang maksimal 200 karakter'),
  item_price_usd: z
    .number({ message: 'Harga tertera wajib diisi' })
    .positive('Harga harus lebih dari 0')
    .max(100000, 'Harga di luar jangkauan jastip — chat admin dulu'),
  quantity: z
    .number({ message: 'Jumlah wajib diisi' })
    .int('Jumlah harus bilangan bulat')
    .min(1, 'Minimal 1 barang')
    .max(100, 'Maksimal 100 barang'),
  notes: z.string().max(500, 'Catatan maksimal 500 karakter').optional(),
  contact: jastipContactSchema,
});

export type JastipQuoteFormData = z.input<typeof jastipQuoteSchema>;
export type JastipQuoteValues = z.output<typeof jastipQuoteSchema>;

/**
 * Nilai awal yang dilempar `/cek-harga` lewat `location.state`. Longgar dengan
 * sengaja: state navigasi datang dari luar (tombol back, deep link, versi lama
 * halaman) jadi diperlakukan sebagai input tak dipercaya dan di-parse, bukan
 * di-cast.
 */
export const jastipPrefillSchema = z.object({
  item_url: z.string().optional(),
  item_name: z.string().optional(),
  item_price_usd: z.number().optional(),
});

export type JastipPrefill = z.infer<typeof jastipPrefillSchema>;
